// Foot — plantar (sole) detail panel for pain capture
// (PAIN_TRACKING_PLAN.md Task 3.2). React-free / JSX-free (hazard H7):
// scripts/validatePainMap.ts and scripts/renderBodyMap.ts import this module
// directly from the repo root. JSX starts in PainMapCanvas.tsx.
//
// Own canvas (D-PAIN-6, "Canvas conventions"): 0 0 160 320, centerline x = 80.
// ONE left foot is drawn; PainMapCanvas mirrors it to the right foot at render
// time via mirrorPath. Every region is a single "-l" region.
//
// This is a LEFT foot seen from BELOW (the sole), so the big toe (medial) is on
// the viewer's LEFT (low x) and the little toe (lateral) on the viewer's RIGHT
// (high x) — the mirror image of the dorsal panel, the same relationship the
// dorsal hand panel has to the palmar one. Toes point up (toe tips near y = 22,
// MTP / ball row near y = 90), midfoot y 90-235, heel y 235-306.
//
// Coverage (Task 3.2, plantar aspect): MTP joint per toe, metatarsal heads
// (1st / 2nd / 5th), subtalar joint, calcaneus, the plantar fascia band
// spanning the heel to the metatarsal heads (the plantar-fasciitis band),
// medial and lateral plantar nerve tracks, the tibial nerve behind the medial
// ankle (tarsal tunnel), and the medial longitudinal arch / posterior tibial
// tendon support.

import type { PainStructure } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { PainRegion } from "./painRegions.js";
import { ellipsePath } from "./painRegions.js";

export const FOOT_PLANTAR_VIEWBOX = "0 0 160 320";

/** A closed rounded-rectangle outline path (foot body / heel blocks). */
function box(x0: number, y0: number, x1: number, y1: number, r: number): string {
  return [
    `M ${x0 + r} ${y0}`,
    `L ${x1 - r} ${y0}`,
    `Q ${x1} ${y0} ${x1} ${y0 + r}`,
    `L ${x1} ${y1 - r}`,
    `Q ${x1} ${y1} ${x1 - r} ${y1}`,
    `L ${x0 + r} ${y1}`,
    `Q ${x0} ${y1} ${x0} ${y1 - r}`,
    `L ${x0} ${y0 + r}`,
    `Q ${x0} ${y0} ${x0 + r} ${y0}`,
    "Z",
  ].join(" ");
}

/** A rounded toe capsule from its tip (yTop) down to the ball of the foot. */
function toe(cx: number, yTop: number, w: number): string {
  const yBase = 74;
  return [
    `M ${cx - w} ${yBase}`,
    `L ${cx - w} ${yTop + 6}`,
    `Q ${cx - w} ${yTop} ${cx} ${yTop}`,
    `Q ${cx + w} ${yTop} ${cx + w} ${yTop + 6}`,
    `L ${cx + w} ${yBase}`,
    "Z",
  ].join(" ");
}

export const FOOT_PLANTAR_OUTLINE: string[] = [
  box(42, 58, 116, 250, 22), // forefoot + midfoot
  box(54, 235, 104, 306, 24), // hindfoot / heel
  toe(50, 26, 8), // big toe (T1) — medial, viewer's left
  toe(67, 22, 6), // T2
  toe(83, 26, 6), // T3
  toe(98, 34, 6), // T4
  toe(111, 44, 6), // little toe (T5) — lateral, viewer's right
];

/** One left-foot-only region. `kind` is read from PAIN_STRUCTURE_META so a
 *  region can never disagree with the taxonomy (validator rule S7). */
function reg(id: string, structure: PainStructure, d: string): PainRegion {
  return { id, structure, kind: PAIN_STRUCTURE_META[structure].kind, side: "left", d };
}

// Toe ray centrelines (medial big toe on the viewer's left -> lateral little
// toe on the viewer's right).
const T1 = 50; // big toe / hallux
const T2 = 67;
const T3 = 83;
const T4 = 98;
const T5 = 111; // little

export const FOOT_PLANTAR_STRUCTURES: PainRegion[] = [
  // ── MTP row (base of each toe — the "ball" of the foot) ────────────────────
  reg("joint-mtp-t1-l", "joint-mtp", ellipsePath(T1, 92, 6, 5.5)),
  reg("joint-mtp-t2-l", "joint-mtp", ellipsePath(T2, 86, 4.5, 4)),
  reg("joint-mtp-t3-l", "joint-mtp", ellipsePath(T3, 88, 4.5, 4)),
  reg("joint-mtp-t4-l", "joint-mtp", ellipsePath(T4, 92, 4.5, 4)),
  reg("joint-mtp-t5-l", "joint-mtp", ellipsePath(T5, 98, 4.5, 4)),

  // ── metatarsal heads (just proximal to the MTP joints) ────────────────────
  reg("bone-metatarsal-mt1-l", "bone-metatarsal", ellipsePath(52, 112, 6, 8)),
  reg("bone-metatarsal-mt2-l", "bone-metatarsal", ellipsePath(68, 108, 5, 8)),
  reg("bone-metatarsal-mt5-l", "bone-metatarsal", ellipsePath(108, 116, 5, 8)),

  // ── hindfoot joint + heel bone ──────────────────────────────────────────
  reg("joint-subtalar-l", "joint-subtalar", ellipsePath(80, 254, 12, 8)),
  reg("bone-calcaneus-l", "bone-calcaneus", ellipsePath(80, 285, 16, 14)),

  // ── plantar fascia (heel -> metatarsal heads: the plantar-fasciitis band) ─
  reg("fascia-plantar-l", "fascia-plantar", ellipsePath(72, 175, 20, 78)),

  // ── nerves ───────────────────────────────────────────────────────────────
  // Medial and lateral plantar nerves fanning across the sole toward the toes.
  reg("nerve-plantar-med-l", "nerve-plantar", ellipsePath(64, 150, 6, 55)),
  reg("nerve-plantar-lat-l", "nerve-plantar", ellipsePath(96, 150, 6, 52)),
  // Tibial nerve behind the medial malleolus (the tarsal tunnel).
  reg("nerve-tibial-l", "nerve-tibial", ellipsePath(58, 245, 6, 22)),

  // ── soft tissue (medial arch / posterior tibial tendon support) ──────────
  reg("tissue-tendon-arch-l", "tissue-tendon", ellipsePath(60, 185, 10, 42)),
];
